// src/context/ProductContext.js
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { getAllProducts } from '../components/sanityProject/sanityData';

const ProductContext = createContext();

const CACHE_KEY = 'productsCache';
const CACHE_TIME_KEY = 'productsCacheTime';
const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes

export const ProductProvider = ({ children }) => {
  // Initialize from sessionStorage
  const cached = sessionStorage.getItem(CACHE_KEY);
  const initialProducts = cached ? JSON.parse(cached) : [];

  // If cached products exist, loading is false immediately
  const [products, setProducts] = useState(initialProducts);
  const [loading, setLoading] = useState(initialProducts.length === 0);

  const updateProducts = useCallback((newProducts) => {
    setProducts(newProducts);
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(newProducts));
    sessionStorage.setItem(CACHE_TIME_KEY, Date.now());
  }, []);

  const fetchProducts = useCallback(async () => {
    try {
      const data = await getAllProducts();
      if (data && Array.isArray(data)) {
        updateProducts(data);
      }
    } catch {
      // error silently ignored
    } finally {
      setLoading(false);
    }
  }, [updateProducts]);

  useEffect(() => {
    const cachedTime = sessionStorage.getItem(CACHE_TIME_KEY);
    const age = cachedTime ? Date.now() - parseInt(cachedTime, 10) : null;
    const isCacheValid = cached && cachedTime && age < CACHE_DURATION;

    // Combine fetch conditions to avoid double fetch
    if (!isCacheValid || (age && age > CACHE_DURATION * 0.8)) {
      fetchProducts();
    }

    const handleStorageChange = (e) => {
      if (e.key === CACHE_KEY) {
        try {
          const updated = JSON.parse(e.newValue);
          if (Array.isArray(updated)) setProducts(updated);
        } catch {
          // error silently ignored
        }
      }
    };
    window.addEventListener('storage', handleStorageChange);

    return () => window.removeEventListener('storage', handleStorageChange);
  }, [fetchProducts, cached]);

  return (
    <ProductContext.Provider value={{ products, loading, fetchProducts, updateProducts }}>
      {children}
    </ProductContext.Provider>
  );
};

export const useProducts = () => useContext(ProductContext);